type ShopSiteEnvironment = {
  NEXT_PUBLIC_SHOP_URL?: string
}

/**
 * The standalone HubHive Shop (shop.hubhive.com) is one public site in every
 * environment — it has no dev twin — so the fallback is the production origin
 * and the override exists for local or preview work. Shared by Next
 * configuration and browser route builders so links and API calls agree.
 */
export function getShopSiteUrl(source: ShopSiteEnvironment = {
  NEXT_PUBLIC_SHOP_URL: process.env.NEXT_PUBLIC_SHOP_URL,
}): string {
  const configured = source.NEXT_PUBLIC_SHOP_URL?.trim()
  const url = new URL(configured || 'https://shop.hubhive.com')
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.search || url.hash) {
    throw new Error('NEXT_PUBLIC_SHOP_URL must be an HTTP(S) site URL without credentials, query, or fragment')
  }
  return url.toString().replace(/\/+$/, '')
}

export function shopUrl(path = '/'): string {
  return `${getShopSiteUrl()}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * The Shop's `client_id` as a **Sign-in client** of Sign in with HubHive
 * (ADR-0150): the `:SignInClient` row the operator creates per environment.
 * A Handoff code is minted for this client and redeemed into the Shop's own
 * sign-in route.
 */
export const SHOP_SIGN_IN_CLIENT_ID = 'hubhive-shop'

/**
 * The `next` a Handoff code carries for a Shop link: the link's path and
 * query relative to the Shop origin, which is all the redeem accepts (Go
 * refuses anything that is not a relative path). `null` when `href` is not
 * on the Shop origin — such a link is not the Shop's and keeps its plain
 * navigation. The fragment is dropped: it never reaches a server, and the
 * redeem's 302 to the Shop sign-in route would not carry it anyway.
 */
export function shopNextPath(href: string): string | null {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  if (url.origin !== new URL(getShopSiteUrl()).origin) return null
  return `${url.pathname}${url.search}`
}
